import { environment } from '../../environment/environment';
import { localStorageGet } from '../../utils';

/** Mirrors ClientSettingsService's RISK_FLAG_KEY - the cache it writes is what this reads. */
const RISK_FLAG_KEY = 'nav.riskProduct';

/**
 * Does this CLIENT get the RiskAI product (the High Risk board)?
 *
 * One function, because the answer has four readers that must never disagree: the
 * RiskAI rail product (via navCtx), the bed board's "Go to High Risk Reports"
 * button, its tile-menu entry, and HighRiskRouteGuard. This is the CLIENT axis
 * only - the ROLE gate (lib isAdmin, re-enforced by requireAdmin on
 * snfs/getHighRiskPatients) applies on top of it.
 *
 * SOURCE OF TRUTH: `region.uiSettings.products.risk` in Mongo, reaching the app
 * through snfs/getClientSettings. NOT config/clients.json: that file is deploy
 * configuration (firebaseProject, apiUrl, hostnames), and one deployment serves
 * several regions - a per-deploy flag cannot express a per-client entitlement at
 * all. It lives beside `comply` and `mds`, which are entitled the same way.
 *
 * OPT-IN, deliberately against the grain of its neighbours. `productEnabled` in
 * navigation/resolve.ts treats an absent flag as ENABLED, which is right for a
 * product every client already has and wrong for one a client buys. So the risk
 * category carries no `productFlag`; the entitlement is filtered in
 * `titlesForRole` instead, and the read here is `=== true`. An unconfigured
 * region gets nothing, and no fleet-wide backfill is needed.
 *
 * WHY localStorage: this is called synchronously - HighRiskRouteGuard.canActivate
 * and BedBoardComponent's field initializer both run a tick before the settings
 * HTTP load can answer. ClientSettingsService caches this one key on every load
 * and reseeds from it on boot, exactly as it already does for `newNavigation`.
 * The cache is a read-ahead, never the authority: the region's value overwrites it
 * on the next load, and it is per-browser.
 */
export function isHighRiskEnabledForClient(): boolean {
  return localStorageGet(RISK_FLAG_KEY) === true
    // TEMPORARY BRIDGE - delete this arm, and the `environment` import above.
    //
    // empire and staging use the High Risk board TODAY, and their region documents
    // do not carry uiSettings.products.risk yet. Without this line they lose the
    // board the moment this ships. staging is covered by the same test: its
    // clients.json entry is snfClient "empire".
    //
    // TO REMOVE (in this order, or empire goes dark):
    //   1. db.regions.updateOne({ regionId: <empire> },
    //        { $set: { "uiSettings.products.risk": true } })   // dot path, not a
    //        whole-object $set - that would wipe smsEnabled and its siblings
    //   2. the same write for staging's region
    //   3. confirm the board still loads for both
    //   4. delete these two lines
    //
    // Cost of keeping it: the entitlement cannot be turned OFF for empire/staging,
    // because this ORs past whatever the region says. It also matches localhost,
    // where environment.ts forces snfClient to EMPIRE - so `ng serve` always shows
    // the entitled state. To preview the unentitled UI while this arm exists,
    // comment it out rather than clearing the localStorage key.
    || environment.snfClient === environment.CLIENTS_NAMES.EMPIRE;
}
